import React from 'react';
import MetaTag from '@/components/ui/MetaTag';
import JsonLd from '@/components/ui/JsonLd';
import { BUSINESS } from '@/constants/business';
import { JournalArticle, PAGE_TOPICS, TOPIC_BY_SLUG, Topic, topicHref } from './const';
import { PROMOS, Promo, PromoImages } from './promo';
import { GoldCard } from './gold-card';
import type { GoldPrices } from './prices';
import { GIA_LOOKUP_HREF } from './market-pages';
import {
  A,
  ArticleCard,
  ArticleGrid,
  Breadcrumb,
  Button,
  Eyebrow,
  ExploreMore,
  FaqSection,
  JournalShell,
  Section,
  SectionHead,
  TopicHero,
  TopicsGrid,
  renderBlock,
} from './components';

const SITE = BUSINESS.appUrl;

/* ------------------------------------------------------------------ */
/* /tin-tuc/                                                           */
/* ------------------------------------------------------------------ */

export type JournalReel = { id: number; cover: string; title: string };

export type JournalHomeProps = {
  reels?: JournalReel[];
  promoImages?: PromoImages;
  /** chương trình từ anthu.today (chuyên mục Khuyến mãi); thiếu → PROMOS trong code */
  promos?: Promo[];
  gold?: GoldPrices | null;
  latest: JournalArticle[];
  articles?: JournalArticle[];
  picks: JournalArticle[];
  q: string;
  results: JournalArticle[];
};

export const JournalHomeLayout = ({ picks, q, results, reels = [], promoImages = {}, promos = PROMOS, gold = null, latest = [], articles = [] }: JournalHomeProps) => {
  // Tin mới (chuyên mục Tin Tức trên anthu.today) — tự hiện khi đăng bài, không cần sửa PROMOS
  const promoSlugs = new Set(promos.map((p) => p.article));
  const news = latest.filter((a) => !a.press && !a.promotion && !promoSlugs.has(a.slug)).slice(0, 3);
  const searching = q.trim().length > 0;
  return (
    <JournalShell activeKey="kien-thuc" query={q}>
      <MetaTag
        title="Kiến thức kim cương, trang sức & tin tức"
        description="Kiến thức kim cương, cảm hứng trang sức và những câu chuyện mới nhất từ An Thư The Diamond Store."
        image={`${SITE}/logo-bg.png`}
        noindex={searching}
      />
      {!searching && (
        <JsonLd
          schema={[
            {
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              '@id': `${SITE}/tin-tuc/#page`,
              url: `${SITE}/tin-tuc/`,
              name: 'Kiến thức kim cương, trang sức & tin tức | An Thư',
              inLanguage: 'vi',
              publisher: { '@id': BUSINESS.id },
              hasPart: PAGE_TOPICS.map((t) => ({ '@type': 'CollectionPage', name: t.title, url: `${SITE}${topicHref(t.slug)}` })),
            },
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: SITE },
                { '@type': 'ListItem', position: 2, name: 'Tin tức', item: `${SITE}/tin-tuc/` },
              ],
            },
          ]}
        />
      )}
      <Breadcrumb items={[{ label: 'Trang chủ', href: '/' }, { label: 'Tin tức' }]} back={false} searchQuery={q} />

      {searching ? (
        <Section id="ket-qua-tim-kiem">
          <SectionHead eyebrow="TÌM KIẾM" title={`Kết quả cho “${q}”`} lead={`${results.length} bài viết phù hợp.`} />
          {results.length ? <ArticleGrid items={results} /> : <p className="tw-text-jr-mute">Chưa có bài phù hợp. Thử từ khoá khác như “kiểm định”, “nhẫn cưới”, “bảo quản”.</p>}
        </Section>
      ) : (
        <>
          <section className="tw-bg-jr-band">
            <div className="tw-mx-auto tw-w-full tw-max-w-[1296px] tw-px-5 md:tw-px-8 tw-grid tw-items-center tw-gap-8 md:tw-grid-cols-[1.2fr_0.8fr] tw-py-10 md:tw-py-14">
              <div>
                <Eyebrow>GÓC AN THƯ</Eyebrow>
                <h1 className="tw-sr-only">Tin tức & kiến thức kim cương, trang sức, đá quý – Góc An Thư</h1>
                <p
                  aria-hidden="true"
                  className="tw-font-jr tw-mt-4 tw-mb-0 tw-text-[34px] md:tw-text-[52px] lg:tw-text-[56px] tw-font-normal tw-leading-[1.15] [text-wrap:balance]"
                >
                  <span className="tw-block">Hiểu giá trị.</span>
                  <span className="tw-block md:tw-whitespace-nowrap">Chọn điều xứng đáng.</span>
                </p>
                <p className="tw-mt-5 tw-mb-0 tw-max-w-[540px] tw-text-[17px] md:tw-text-[19px] tw-text-jr-mute tw-leading-relaxed">
                  Kiến thức kim cương, cảm hứng trang sức và những câu chuyện mới nhất từ An Thư.
                </p>
                <div className="tw-mt-8">
                  <Button href="#bai-viet">Khám phá bài viết</Button>
                </div>
              </div>
              <div className="tw-flex tw-justify-center md:tw-justify-end">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/journal/hero-ring.jpg" alt="Nhẫn kim cương An Thư" width={520} height={520} className="tw-w-full tw-max-w-[460px] tw-aspect-square tw-object-cover" />
              </div>
            </div>
          </section>

          <Section id="chuyen-de">
            <SectionHead title="Bạn muốn tìm hiểu điều gì?" lead="Sáu chuyên đề giúp bạn đi từ tìm hiểu đến lựa chọn." />
            <TopicsGrid />
          </Section>

          <Section className="!tw-pt-4">
            <div className="tw-grid tw-gap-8 md:tw-grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] md:tw-items-center tw-bg-[#EFEFEF] tw-p-6 md:tw-p-12">
              <div>
                <Eyebrow>GIÁ VÀNG & THỊ TRƯỜNG</Eyebrow>
                <h2 className="tw-font-jr tw-mt-4 tw-mb-0 tw-text-[32px] md:tw-text-[44px] tw-font-normal tw-leading-[1.2]">Giá vàng hôm nay.</h2>
                <p className="tw-mt-4 tw-mb-0 tw-text-[17px] tw-text-jr-mute">Giá mua vào, bán ra theo thương hiệu.</p>
                <p className="tw-mt-6 tw-mb-0">
                  <A href="/tin-tuc/vang-bao-quan/" className="tw-text-[16px] tw-font-medium tw-text-jr-green hover:tw-underline">
                    Vàng 14K · 18K · 24K →
                  </A>
                </p>
                <div className="tw-mt-6">
                  <Button href="/bang-gia/" className="md:tw-min-w-[360px]">
                    Xem đầy đủ giá vàng →
                  </Button>
                </div>
              </div>
              <GoldCard gold={gold} variant="band" />
            </div>
          </Section>

          <Section className="!tw-pt-4">
            <div className="tw-mb-8 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
              <div>
                <Eyebrow>GÓC AN THƯ</Eyebrow>
                <h2 className="tw-font-jr tw-mt-2 tw-mb-0 tw-text-[28px] md:tw-text-[40px] tw-font-normal tw-leading-[1.2]">Ưu đãi tại An Thư</h2>
              </div>
              <A href="/tin-tuc/khuyen-mai/" className="tw-text-[15px] tw-font-medium tw-text-jr-green hover:tw-underline">
                Xem tất cả Khuyến mãi →
              </A>
            </div>
            <div className="tw-grid tw-gap-5 md:tw-grid-cols-2">
              {promos.filter((p) => !p.ended).map((p) => (
                <div key={p.slug} className="tw-grid tw-grid-cols-[minmax(0,42%)_1fr] tw-items-center tw-gap-5 md:tw-gap-8 tw-border tw-border-solid tw-border-[#DECA95] tw-bg-[#FEFCEF] tw-p-3 md:tw-p-5">
                  <A href={`/article/${p.article}/`} className="tw-block">
                    {promoImages[p.slug] ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={promoImages[p.slug]} alt={p.short} width={1080} height={1350} loading="lazy" className="tw-w-full tw-h-auto tw-aspect-[4/5] tw-object-cover" />
                    ) : (
                      <div className="tw-aspect-[4/5] tw-w-full tw-bg-jr-band" />
                    )}
                  </A>
                  <div>
                    <h3 className="tw-font-jr tw-m-0 tw-text-[24px] md:tw-text-[34px] tw-font-normal tw-leading-[1.2] tw-text-jr-green">{p.short}</h3>
                    <p className="tw-mt-4 tw-mb-0 tw-whitespace-pre-line tw-text-[15px] md:tw-text-[18px] tw-text-jr-mute">{p.period.replace(/^Áp dụng từ (\S+) đến /, 'Áp dụng từ $1\nĐến ').replace(/\.$/, '').replace(/ - /, ' – ')}</p>
                    <p className="tw-mt-4 tw-mb-0">
                      <span className="tw-inline-block tw-bg-[#F1E6C4] tw-px-3 tw-py-1.5 tw-text-[14px] tw-text-[#8a7340]">Đang diễn ra</span>
                    </p>
                    <A href={`/article/${p.article}/`} className="tw-mt-5 tw-inline-flex tw-min-h-[48px] tw-items-center tw-bg-jr-green tw-px-6 tw-text-[15px] tw-font-medium tw-text-white hover:tw-opacity-90">
                      {p.shortCta || p.cta} →
                    </A>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {news.length > 0 && (
            <Section className="!tw-pt-4">
              <div className="tw-mb-8 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
                <div>
                  <Eyebrow>MỚI NHẤT</Eyebrow>
                  <h2 className="tw-font-jr tw-mt-2 tw-mb-0 tw-text-[28px] md:tw-text-[40px] tw-font-normal tw-leading-[1.2]">Tin mới từ An Thư</h2>
                </div>
                <A href="/tin-tuc/tin-an-thu/" className="tw-text-[15px] tw-font-medium tw-text-jr-green hover:tw-underline">
                  Xem tất cả →
                </A>
              </div>
              <div className="tw-grid tw-gap-x-5 tw-gap-y-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
                {news.map((a) => (
                  <ArticleCard key={a.id} a={a} fit="poster" showDate />
                ))}
              </div>
            </Section>
          )}

          {(articles.length > 0 || picks.length > 0) && (
            <Section id="bai-viet" className="!tw-pt-4 tw-scroll-mt-6">
              <SectionHead title="Bài viết mới nhất" />
              <div className="tw-grid tw-gap-x-5 tw-gap-y-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
                {(articles.length ? articles : picks).map((a) => (
                  <ArticleCard key={a.id} a={a} />
                ))}
              </div>
            </Section>
          )}

          <Section className="!tw-pt-4">
            <div className={`tw-grid tw-gap-8 tw-items-center tw-bg-[#0b0b0b] tw-text-white tw-p-6 md:tw-p-10 ${reels.length ? 'md:tw-grid-cols-[1fr_auto_auto]' : ''}`}>
              <div>
                <Eyebrow>AN THƯ REELS</Eyebrow>
                <h2 className="tw-font-jr tw-mt-4 tw-mb-0 tw-text-[28px] tw-font-normal tw-leading-[1.2] md:tw-text-[36px] tw-text-white">
                  Chiêm ngưỡng
                  <br /> trang sức chuyển động.
                </h2>
                <p className="tw-mt-6 tw-mb-0 tw-text-[16px] tw-text-[#d7d3c8]">
                  {reels.length ? 'Hai góc nhìn thật từ An Thư. Chọn video để xem trọn vẹn vẻ đẹp.' : 'Ảnh và video thật từ An Thư.'}
                </p>
                <div className="tw-mt-8">
                  <Button href="/reels/" tone="gold">
                    Xem tất cả video
                  </Button>
                </div>
              </div>
              {reels.map((r) => (
                <A key={r.id} href={`/reels/?v=${r.id}`} className="tw-group tw-block tw-w-full md:tw-w-[210px] lg:tw-w-[250px] tw-border tw-border-[#2a2a2a] tw-p-3">
                  <div className="tw-relative">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={r.cover} alt={`Video ${r.title}`} loading="lazy" className="tw-aspect-[3/4] tw-w-full tw-object-cover" />
                    <span className="tw-absolute tw-left-1/2 tw-top-1/2 tw-flex tw-h-12 tw-w-12 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-white tw-bg-black/40 tw-text-white">
                      ▶
                    </span>
                  </div>
                  <p className="tw-mt-4 tw-mb-0 tw-text-[14px] tw-text-white">{r.title}</p>
                  <p className="tw-mt-3 tw-mb-0 tw-text-[13px] tw-text-[#d7d3c8] group-hover:tw-underline">Xem video →</p>
                </A>
              ))}
            </div>
          </Section>
          <ExploreMore />
        </>
      )}
    </JournalShell>
  );
};

/* ------------------------------------------------------------------ */
/* /tin-tuc/[chuyên đề]/                                               */
/* ------------------------------------------------------------------ */

export type JournalTopicProps = { slug: string; articles: JournalArticle[] };

export const JournalTopicLayout = ({ slug, articles }: JournalTopicProps) => {
  const topic = TOPIC_BY_SLUG[slug];
  const navKey = ['kim-cuong', 'trang-suc'].includes(topic.slug) ? topic.slug : 'kien-thuc';
  const url = `${SITE}${topicHref(topic.slug)}`;
  return (
    <JournalShell activeKey={navKey}>
      <MetaTag title={topic.seoTitle} description={topic.seoDescription} image={`${SITE}/logo-bg.png`} />
      <JsonLd
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: SITE },
              { '@type': 'ListItem', position: 2, name: 'Tin tức', item: `${SITE}/tin-tuc/` },
              { '@type': 'ListItem', position: 3, name: topic.title, item: url },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': `${url}#page`,
            name: topic.seoTitle,
            description: topic.seoDescription,
            url,
            inLanguage: 'vi',
            publisher: { '@id': BUSINESS.id },
            mainEntity: { '@id': `${url}#articles` },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            '@id': `${url}#articles`,
            name: `Bài viết về ${topic.title.toLowerCase()}`,
            itemListElement: articles.slice(0, 12).map((a, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `${SITE}/article/${a.slug}/`,
              name: a.title,
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            '@id': `${url}#faq`,
            url,
            inLanguage: 'vi',
            mainEntity: topic.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
          },
        ]}
      />
      <Breadcrumb items={[{ label: 'Trang chủ', href: '/' }, { label: 'Tin tức', href: '/tin-tuc/' }, { label: topic.title }]} />
      {topic.slug === 'trang-suc' ? <TrangSucHero topic={topic} /> : <TopicHero topic={topic} />}
      {(topic.slug === 'kim-cuong' || topic.slug === 'kiem-dinh') && (
        <Section className="!tw-py-0 tw-mt-6">
          <div className="tw-flex tw-flex-col tw-gap-4 tw-bg-jr-band tw-p-5 md:tw-flex-row md:tw-items-center md:tw-justify-between md:tw-px-8">
            <p className="tw-m-0 tw-text-[16px] tw-text-jr-ink">Đã có số báo cáo? Đối chiếu thông tin viên đá trên GIA.</p>
            <Button href={GIA_LOOKUP_HREF} className="tw-shrink-0">Tra cứu GIA</Button>
          </div>
        </Section>
      )}
      {topic.blocks.map((b, i) => renderBlock(b, { topic, articles, key: i }))}
      <FaqSection items={topic.faq} />
      <ExploreMore exclude={topic.slug} />
    </JournalShell>
  );
};

const TrangSucHero = ({ topic }: { topic: Topic }) => (
  <section>
    <div className="tw-mx-auto tw-w-full tw-max-w-[1296px] tw-px-5 md:tw-px-8 tw-pt-6 md:tw-pt-8 tw-pb-10">
      <Eyebrow>{topic.hero.eyebrow}</Eyebrow>
      <h1 className="tw-font-jr tw-mt-4 tw-mb-0 tw-text-[34px] md:tw-text-[56px] tw-font-normal tw-leading-[1.15]">{topic.hero.title.join(' ')}</h1>
      <p className="tw-mt-4 tw-mb-0 tw-text-[17px] md:tw-text-[19px] tw-text-jr-mute">{topic.hero.lead}</p>
      <div className="tw-mt-10 tw-grid tw-gap-4 md:tw-grid-cols-[1fr_1fr]">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/journal/necklace.jpg" alt="Dây chuyền kim cương" className="tw-w-full tw-aspect-square tw-object-cover tw-bg-black" />
        <div className="tw-grid tw-gap-4 tw-grid-rows-[auto_1fr]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/journal/earrings.jpg" alt="Bông tai kim cương" className="tw-w-full tw-aspect-[16/10] tw-object-cover tw-bg-black" />
          <div className="tw-bg-jr-band tw-p-8 md:tw-p-10">
            <Eyebrow>CẢM HỨNG PHONG CÁCH</Eyebrow>
            <p className="tw-font-jr tw-mt-4 tw-mb-0 tw-text-[28px] md:tw-text-[34px] tw-leading-[1.2]">
              Thanh lịch trong
              <br /> từng lựa chọn.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
